/**
 * PriceMismatchConfirmModal
 *
 * Blocking confirmation shown when the user clicks "Submit to Sage" on an
 * order with price irregularities. Two kinds of issues get surfaced here:
 *
 *   1. off_contract — the unit price matches no known source (Special
 *      Pricing, Quote, Standard).
 *   2. po_waterfall_mismatch — the unit price is on-contract
 *      (waterfall-seeded) but the LLM-extracted PO price disagreed.
 *
 * Default action policy:
 *   - If ANY off-contract lines are present → default = "Go back and fix"
 *     (genuinely risky, force explicit proceed).
 *   - If ONLY PO-vs-waterfall mismatches → default = "Submit anyway"
 *     (the waterfall price is correct by design; the modal is informational
 *     so the operator just needs to eyeball it and move on).
 *
 * Esc always triggers cancel. Enter triggers the default action.
 */

import { useEffect, useRef } from 'react';
import {
  sourceLabel,
  type OffContractMismatch,
  type PoWaterfallMismatch,
  type PriceMismatchItem,
} from '../../utils/priceMismatchCheck';
import type { PricingSourceType } from '../../services/portalOrders';
import styles from './PriceMismatchConfirmModal.module.css';

export interface PriceMismatchConfirmModalProps {
  mismatches: PriceMismatchItem[];
  onCancel: () => void;
  onConfirm: () => void;
  /** Disables both buttons while the parent submits. */
  submitting?: boolean;
  /** The order being submitted, recorded as the row's target (kanban f0b295e8). */
  orderId?: string | null;
  /** Price formatter shared with OrderDetailModal for consistency. */
  formatPrice: (val: string | number | null | undefined) => string;
}

// Only "real" source types appear in buildPricingOptions output
// (special, tiered, quote, base_discount, standard). 'custom' / 'none' never do.
type ListedSource = Exclude<PricingSourceType, 'custom' | 'none'>;

const SOURCE_BADGE_CLASS: Record<ListedSource, string> = {
  special: styles.badgeSpecial,
  tiered: styles.badgeSpecial,
  quote: styles.badgeQuote,
  base_discount: styles.badgeSpecial,
  standard: styles.badgeStandard,
};

const SOURCE_BADGE_TEXT: Record<ListedSource, string> = {
  special: 'SP',
  tiered: 'SP',
  quote: 'Qt',
  base_discount: 'SP',
  standard: 'Std',
};

function badgeClassFor(type: PricingSourceType): string {
  return SOURCE_BADGE_CLASS[type as ListedSource] ?? '';
}
function badgeTextFor(type: PricingSourceType): string {
  return SOURCE_BADGE_TEXT[type as ListedSource] ?? '';
}

function OffContractRow({
  m,
  formatPrice,
}: {
  m: OffContractMismatch;
  formatPrice: (val: string | number | null | undefined) => string;
}) {
  return (
    <li className={styles.item} data-testid={`pmcm-off-contract-${m.lineItemId}`}>
      <div className={styles.itemHead}>
        <div>
          <span className={styles.itemCode}>{m.itemCode}</span>
          {m.itemDescription && (
            <span className={styles.itemDesc}> — {m.itemDescription}</span>
          )}
        </div>
        <div className={styles.orderedBlock}>
          <div className={styles.orderedLabel}>Ordered</div>
          <div className={styles.orderedPrice}>{formatPrice(m.orderedPrice)}</div>
        </div>
      </div>

      <div className={styles.sourceSection}>
        <div className={styles.sourceSectionLabel}>Known prices for this item:</div>
        {m.availableSources.length === 0 ? (
          <div className={styles.noSource}>
            No Special Pricing, Quote, or Standard price on file for this customer.
          </div>
        ) : (
          <ul className={styles.sourceList}>
            {m.availableSources.map((s, i) => (
              <li
                key={`${s.type}-${s.quote_source_id ?? i}`}
                className={styles.sourceRow}
              >
                <span className={`${styles.sourceBadge} ${badgeClassFor(s.type)}`}>
                  {badgeTextFor(s.type)}
                </span>
                <div className={styles.sourceLabels}>
                  <span className={styles.sourceLabel}>{s.label}</span>
                  {s.sublabel && (
                    <span className={styles.sourceSublabel}>{s.sublabel}</span>
                  )}
                </div>
                <span className={styles.sourcePrice}>{formatPrice(s.price)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function PoWaterfallRow({
  m,
  formatPrice,
}: {
  m: PoWaterfallMismatch;
  formatPrice: (val: string | number | null | undefined) => string;
}) {
  const ourLabel = sourceLabel(m.currentSource);
  return (
    <li className={styles.item} data-testid={`pmcm-po-mismatch-${m.lineItemId}`}>
      <div className={styles.itemHead}>
        <div>
          <span className={styles.itemCode}>{m.itemCode}</span>
          {m.itemDescription && (
            <span className={styles.itemDesc}> — {m.itemDescription}</span>
          )}
        </div>
      </div>
      <div className={styles.poCompare}>
        <div className={`${styles.poCompareBlock} ${styles.poCompareBlockShip}`}>
          <div className={styles.poCompareLabel}>{ourLabel}</div>
          <div className={styles.poCompareValue}>{formatPrice(m.orderedPrice)}</div>
          <div className={styles.poCompareHint}>
            {m.priceSourceDetail ?? 'what we\u2019ll ship at'}
          </div>
        </div>
        <div className={`${styles.poCompareBlock} ${styles.poCompareBlockPo}`}>
          <div className={styles.poCompareLabel}>Discovered on PO</div>
          <div className={styles.poCompareValue}>{formatPrice(m.poPrice)}</div>
          <div className={styles.poCompareHint}>LLM-extracted from the PO</div>
        </div>
      </div>
    </li>
  );
}

export default function PriceMismatchConfirmModal({
  mismatches,
  onCancel,
  onConfirm,
  submitting = false,
  orderId = null,
  formatPrice,
}: PriceMismatchConfirmModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const proceedBtnRef = useRef<HTMLButtonElement>(null);

  const offContract = mismatches.filter(
    (m): m is OffContractMismatch => m.kind === 'off_contract',
  );
  const poMismatches = mismatches.filter(
    (m): m is PoWaterfallMismatch => m.kind === 'po_waterfall_mismatch',
  );
  const totalCount = mismatches.length;

  // Default action: proceed if the only issue is PO-vs-waterfall (the
  // waterfall price is correct by design, so the modal is informational).
  // Otherwise — any off-contract lines present — default to cancel.
  const defaultAction: 'proceed' | 'cancel' =
    offContract.length === 0 && poMismatches.length > 0 ? 'proceed' : 'cancel';

  // Focus the default action's button on mount.
  useEffect(() => {
    if (defaultAction === 'proceed') {
      proceedBtnRef.current?.focus();
    } else {
      cancelBtnRef.current?.focus();
    }
  }, [defaultAction]);

  // Esc always cancels. Enter triggers the default action.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (submitting) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (defaultAction === 'proceed') {
          onConfirm();
        } else {
          onCancel();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm, submitting, defaultAction]);

  let titleText: string;
  let headline: string;
  if (offContract.length > 0 && poMismatches.length === 0) {
    titleText = "Price doesn't match any known source";
    headline =
      offContract.length === 1
        ? "The price on this line doesn't match any Special Price or Quote we have."
        : `The prices on ${offContract.length} lines don't match any Special Price or Quote we have.`;
  } else if (poMismatches.length > 0 && offContract.length === 0) {
    titleText = 'PO price differs from what we\u2019re submitting';
    headline =
      poMismatches.length === 1
        ? "The price we\u2019re submitting differs from what the PO said on this line."
        : `On ${poMismatches.length} lines, the price we\u2019re submitting differs from what the PO said.`;
  } else {
    titleText = 'Price issues need your confirmation';
    headline = `${totalCount} lines have pricing issues that need your confirmation before submit.`;
  }

  return (
    <div
      className={styles.overlay}
      // Intentionally do NOT close on overlay click — the user must
      // make an explicit choice between "Go back" and "Submit anyway".
      role="dialog"
      aria-modal="true"
      aria-labelledby="pmcmTitle"
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div className={styles.icon} aria-hidden="true">!</div>
          <div className={styles.title} id="pmcmTitle">
            {titleText}
          </div>
        </div>

        <div className={styles.body}>
          <p className={styles.lede}>{headline}</p>

          {offContract.length > 0 && (
            <>
              {poMismatches.length > 0 && (
                <div className={styles.sectionHeading}>
                  Off-contract prices ({offContract.length})
                </div>
              )}
              <ul className={styles.itemList}>
                {offContract.map((m) => (
                  <OffContractRow key={m.lineItemId} m={m} formatPrice={formatPrice} />
                ))}
              </ul>
            </>
          )}

          {poMismatches.length > 0 && (
            <>
              {offContract.length > 0 && (
                <div className={styles.sectionHeading}>
                  PO vs. shipping price ({poMismatches.length})
                </div>
              )}
              <ul className={styles.itemList}>
                {poMismatches.map((m) => (
                  <PoWaterfallRow key={m.lineItemId} m={m} formatPrice={formatPrice} />
                ))}
              </ul>
            </>
          )}

          <p className={styles.askAgain}>Are you sure you want to submit anyway?</p>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            ref={proceedBtnRef}
            className={`${styles.btn} ${
              defaultAction === 'proceed' ? styles.btnCancel : styles.btnProceed
            }`}
            onClick={onConfirm}
            disabled={submitting}
            data-testid="pmcm-proceed"
            data-default-action={defaultAction === 'proceed' ? 'true' : 'false'}
            data-action="order_submit_despite_price_mismatch"
            data-target-order-id={orderId ?? undefined}
            data-outcome="request"
          >
            {submitting
              ? 'Submitting…'
              : defaultAction === 'proceed'
              ? 'Submit'
              : 'Yes, submit anyway'}
          </button>
          <button
            type="button"
            ref={cancelBtnRef}
            className={`${styles.btn} ${
              defaultAction === 'cancel' ? styles.btnCancel : styles.btnProceed
            }`}
            onClick={onCancel}
            disabled={submitting}
            data-action="order_price_mismatch_go_back"
            data-target-order-id={orderId ?? undefined}
            data-testid="pmcm-cancel"
            data-default-action={defaultAction === 'cancel' ? 'true' : 'false'}
          >
            Go back and fix
          </button>
        </div>

        <div className={styles.hint}>
          <span className={styles.kbd}>Esc</span> cancels.{' '}
          <span className={styles.kbd}>Enter</span>{' '}
          {defaultAction === 'proceed' ? (
            <>submits — the PO discovered a different price, but our contract price is what we ship.</>
          ) : (
            <>cancels. To submit anyway you must explicitly click <b>Yes, submit anyway</b>.</>
          )}
        </div>
      </div>
    </div>
  );
}
